import React, { useContext, useEffect, useMemo, useState } from "react";
import { IntlContext } from "context";
import RegisterClientInformations from "./Infos";
import ServiceSelection from "./ServiceSelection";
import RegisterClientNeed from "./Need";
import RegisterClientFrequency from "./Frequency";
import RegisterClientCesu from "./Cesu";
import Welcome from "./Welcome";
import RegisterStepper, { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterCsrfRefresh } from "../common/useRegisterCsrfRefresh";
import { useClientRegisterSteps } from "../common/useClientRegisterSteps";
import type { RegisterDraft } from "../types";

const CLIENT_DRAFT_STORAGE_KEY = "twoghether:register:client:draft:v1";
const CLIENT_STEP_STORAGE_KEY = "twoghether:register:client:step:v1";

function readLocalStorageJson<T>(key: string): T | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return undefined;
        return JSON.parse(raw) as T;
    } catch {
        return undefined;
    }
}

function writeLocalStorageJson(key: string, value: unknown) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore storage quota / privacy mode errors
    }
}

function removeLocalStorageItem(key: string) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

const ClientSteps: React.FC<{
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
    clearClientDraft: () => void;
}> = ({ client, setClient, clearClientDraft }) => {
    const stepper = useRegisterStepper();

    switch (stepper.activeKey) {
        case "services":
            return <ServiceSelection client={client} setClient={setClient} />;
        case "need":
            return <RegisterClientNeed client={client} setClient={setClient} />;
        case "frequency":
            return <RegisterClientFrequency client={client} setClient={setClient} />;
        case "informations":
            return <RegisterClientInformations client={client} setClient={setClient} />;
        case "cesu":
            return <RegisterClientCesu client={client} setClient={setClient} />;
        case "confirmation":
            return (
                <Welcome
                    onClearDraft={() => {
                        clearClientDraft();
                        removeLocalStorageItem(CLIENT_STEP_STORAGE_KEY);
                    }}
                />
            );
        default:
            return <ServiceSelection client={client} setClient={setClient} />;
    }
};

type RegisterClientModuleProps = {
    onClose?: () => void;
};

const RegisterClientModule: React.FC<RegisterClientModuleProps> = ({ onClose }) => {
    const intl = useContext(IntlContext);
    const steps = useClientRegisterSteps(intl);
    useRegisterCsrfRefresh();

    const [client, setClient] = useState<RegisterDraft | undefined>(() =>
        readLocalStorageJson<RegisterDraft>(CLIENT_DRAFT_STORAGE_KEY)
    );

    const initialStepKey = useMemo(() => {
        const stored = readLocalStorageJson<string>(CLIENT_STEP_STORAGE_KEY);
        return typeof stored === "string" && stored.trim() ? stored : "services";
    }, []);

    useEffect(() => {
        if (client == null) {
            removeLocalStorageItem(CLIENT_DRAFT_STORAGE_KEY);
            return;
        }
        writeLocalStorageJson(CLIENT_DRAFT_STORAGE_KEY, client);
    }, [client]);

    return (
        <RegisterStepper
            title="Create your client profile"
            type="user"
            hero={{
                imageSrc: "/images/bg-register.png",
                brandText: "Twoghether",
            }}
            steps={steps}
            initialStepKey={initialStepKey}
            className="overflow-y-auto"
            onClose={onClose}
            onStepChange={(key) => writeLocalStorageJson(CLIENT_STEP_STORAGE_KEY, key)}
        >
            <ClientSteps client={client} setClient={setClient} clearClientDraft={() => setClient(undefined)} />
        </RegisterStepper>
    );
};

export default RegisterClientModule;
